import { useEffect, useLayoutEffect, useRef, useState } from "react"
import type { PlannedShot } from "../types"
import { coachFor, glareArea, ROOM_HINT_MS, stepBackLevel, type Coach, type StepBackLevel } from "./coach"
import { FrameSampler, type Crop, type FrameSample } from "./frame-sampler"
import { focusIssue, glareIssue, glareShare, lightIssue, motionBetween, QUALITY, type FrameMetrics } from "./frame-metrics"
import { isClipped, judgeCloser, judgeFit, type Box, type Fit } from "./framing"
import { initialLock, lockProgress, stepLock, type LockState, type LockView } from "./lock-machine"
import { iou, pickTarget } from "./detector"
import { nextCarAspect, TARGET, targetFrame, type Insets, type TargetFrame } from "./target-frame"
import { alignTo, blend, CONTOUR, followBox, outlineGap, type Points } from "../brain/contour"
import { judgeLiveView, type LiveViewJudgement } from "../brain/combine"
import type { VisionClient } from "../brain/vision-client"
import type { FrameResult } from "../brain/vision-protocol"
import type { Corner } from "../types"

// Turns frame samples into what the camera screen shows: the coaching line,
// the three checks (right spot · sharp · good light), their car's own traced
// outline, the target to fit it in, the shutter countdown, and fires the
// auto-snap. Runs a few times a second; nothing here re-renders per video
// frame.
//
// Whole-car photos (smart camera): the detector follows the car; the
// segmenter traces it (on a crop around the car, so the car fills its
// input) about once a second, and the outline follows the detector's box in
// between. The car must fit the target (framing.ts judgeFit). When it does,
// the car-view brain checks it's the corner asked for, before any photo
// (combine.ts judgeLiveView, the same rules as after the photo).
//
// The models run in the vision engine's worker (brain/vision-client.ts):
// each sample sends a look (only one out at a time) and the answer lands a
// moment later; the camera keeps sampling meanwhile and uses the latest.

export interface LiveView {
  coach: Coach
  // The car the detector is following (normalized to the visible frame).
  box: Box | null
  // Their car: its traced outline (0–1 of the visible frame) and the box
  // the fit is judged on. Outline null when there's no fresh trace.
  car: { outline: Points | null; box: Box | null }
  // Where the whole car should go (whole-car photos, smart camera).
  target: TargetFrame | null
  fit: Fit | null
  // The brain's live answer for this corner, null: none yet.
  view: LiveViewJudgement | null
  // The three plain checks under the picture. `spot`: the whole car framed
  // and the corner asked for (null for steps that don't need the whole car).
  // `light`: not too dark, no reflection.
  checks: { spot: boolean | null; sharp: boolean; light: boolean }
  lockPhase: LockState["phase"] | null
  // 0–1 shutter ring fill.
  progress: number
  debug: {
    luma: number
    sharpness: number
    motion: number
    // Share of the glare area blown out (0–1).
    glare: number
    inferenceMs: number | null
    score: number | null
    segmentMs: number | null
    viewMs: number | null
    // The car's measured width over height.
    aspect: number | null
  }
}

// The car's box counts for this long after the frame that found it (or four
// looks' time on a slow phone).
const DETECT_FRESH_MS = 1200
// …but never longer than this (a stuck engine is let go at 8 s anyway).
const DETECT_STALE_MAX_MS = 3000

// A few moments of not finding the car (too close to be found) don't end a
// stretch of "too close".
const TOO_CLOSE_GRACE_MS = 3000
// How fast the "how much too big" reading follows (per look).
const OVER_EMA = 0.3
// Backing up is helping when the car got a tenth smaller over this long.
const PROGRESS_MS = 2000
// Once the hint is up, it goes only when the car is this much of its size
// then (backing up clearly worked after all).
const HINT_CLEARS = 0.8

// Too close and not getting better: the car is no smaller than it was
// PROGRESS_MS ago (less a tenth).
function stuckIn(trail: ReadonlyArray<{ t: number; over: number }>, t: number): boolean {
  const then = trail.find((p) => t - p.t >= PROGRESS_MS)
  const now = trail[trail.length - 1]
  return !!then && !!now && now.over > then.over * 0.9
}

// Per sample (~5 a second): the remembered sharpest view halves in ~3 s.
const BEST_FADE = 0.955

const SEGMENT = {
  // How often the segmenter may run, and how old its outline may get.
  EVERY_MS: 900,
  FRESH_MS: 2000,
  // The crop around the car it looks at (square, this many pixels).
  SIZE: 256,
  // Padding round the car's box.
  PAD: 0.15,
  // The traced car and the detector's box must agree this well.
  AGREE: 0.5,
} as const

const VIEW = {
  // The brain looks again this often while the car fits…
  EVERY_MS: 2000,
  // …and an answer is good for this long.
  FRESH_MS: 4000,
  // The whole frame, at the width the brain was trained on.
  WIDTH: 500,
} as const

const IDLE_VIEW: LiveView = {
  coach: { line: "", tone: "neutral", ready: false },
  box: null,
  car: { outline: null, box: null },
  target: null,
  fit: null,
  view: null,
  checks: { spot: null, sharp: false, light: false },
  lockPhase: null,
  progress: 0,
  debug: {
    luma: 0,
    sharpness: 0,
    motion: 0,
    glare: 0,
    inferenceMs: null,
    score: null,
    segmentMs: null,
    viewMs: null,
    aspect: null,
  },
}

interface Trace {
  t: number
  // The outline in the visible frame, and the detector's box when traced.
  points: Points
  at: Box
}

function boundsOf(points: Points): Box {
  let x0 = Infinity
  let y0 = Infinity
  let x1 = -Infinity
  let y1 = -Infinity
  for (let i = 0; i < points.length; i += 2) {
    x0 = Math.min(x0, points[i])
    x1 = Math.max(x1, points[i])
    y0 = Math.min(y0, points[i + 1])
    y1 = Math.max(y1, points[i + 1])
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 }
}

// A square crop round the car (0–1 of the visible frame), in video pixels
// as tall as it is wide.
function cropAround(box: Box, crop: Crop, pad: number): Box {
  const side = Math.max(box.w * crop.sw, box.h * crop.sh) * (1 + 2 * pad)
  const w = side / crop.sw
  const h = side / crop.sh
  return { x: box.x + box.w / 2 - w / 2, y: box.y + box.h / 2 - h / 2, w, h }
}

// Draws the video under `roi` (0–1 of the visible frame) into a size×size
// canvas, the source clipped to the video (some browsers draw nothing when
// it runs past the picture); outside the video stays the fill.
function drawCrop(ctx: CanvasRenderingContext2D, video: HTMLVideoElement, crop: Crop, roi: Box, size: number) {
  const sx = crop.sx + roi.x * crop.sw
  const sy = crop.sy + roi.y * crop.sh
  const sw = roi.w * crop.sw
  const sh = roi.h * crop.sh
  const x0 = Math.max(0, sx)
  const y0 = Math.max(0, sy)
  const x1 = Math.min(video.videoWidth, sx + sw)
  const y1 = Math.min(video.videoHeight, sy + sh)
  if (x1 <= x0 || y1 <= y0) return
  const kx = size / sw
  const ky = size / sh
  ctx.drawImage(video, x0, y0, x1 - x0, y1 - y0, (x0 - sx) * kx, (y0 - sy) * ky, (x1 - x0) * kx, (y1 - y0) * ky)
}

export function useLiveCoach({
  video,
  running,
  shot,
  attempt,
  firstShot,
  vision,
  screen,
  insets,
  torch,
  room,
  onAutoSnap,
}: {
  video: HTMLVideoElement | null
  // Camera live and nothing covering it (no walk card, no review strip).
  running: boolean
  shot: PlannedShot
  // Bumped on every retake: the lock starts over.
  attempt: number
  // First shot of this camera session (nothing to "move away" from).
  firstShot: boolean
  // The vision engine, when the smart camera has it.
  vision: VisionClient | null
  // The screen (px) and what covers it (the instruction, the controls):
  // the target sits in the space between.
  screen: { w: number; h: number }
  insets: Insets
  torch: boolean
  // What else could help when there's no room to back up: the wide lens
  // (its button's label, when it's on screen and not in use), turning
  // sideways (held upright).
  room: { widen: string | null; canTurn: boolean }
  onAutoSnap: () => void
}): LiveView {
  const [view, setView] = useState<LiveView>(IDLE_VIEW)
  const lockRef = useRef<LockState>(initialLock(performance.now(), firstShot))
  const bestRef = useRef(0)
  const prevThumb = useRef<Float32Array | null>(null)
  const boxRef = useRef<Box | null>(null)
  // When the frame that found it was taken.
  const boxAt = useRef(-Infinity)
  // How long the last look took (a corner check on a slow phone: a second
  // or more), so a box isn't counted stale while its next look is out.
  const lookMs = useRef(0)
  const scoreRef = useRef<number | null>(null)
  const traceRef = useRef<Trace | null>(null)
  // When a trace / a corner check was last asked for (a miss waits as long
  // as a hit).
  const segTriedAt = useRef(-Infinity)
  const viewTriedAt = useRef(-Infinity)
  const aspectRef = useRef<number | null>(null)
  const fitRef = useRef(false)
  const glareRef = useRef(false)
  const lastFitAt = useRef(0)
  // Too close: since when, when last (a moment without the car, too close
  // to be found, doesn't end it), how much too big (smoothed) and the line
  // for it (steady).
  const tooClose = useRef<{
    since: number
    last: number
    over: number
    level: StepBackLevel
    // The smoothed reading lately (is backing up helping?).
    trail: Array<{ t: number; over: number }>
    // Showing what else would help (the wide lens, turning sideways) since
    // the car was this big: it stays until backing up clearly works.
    hintOver: number | null
  } | null>(null)
  // This shot has already been told how to make room (the wide lens,
  // turning sideways): another stretch of too close hears it at once.
  const roomHinted = useRef(false)
  const viewRef = useRef<{ t: number; judgement: LiveViewJudgement; ms: number | null } | null>(null)
  const msRef = useRef<FrameResult["ms"] | null>(null)
  // One look at a time; answers for an older shot (or a retake) are dropped.
  const asking = useRef(false)
  const generation = useRef(0)
  const segCanvas = useRef<HTMLCanvasElement | null>(null)
  const viewCanvas = useRef<HTMLCanvasElement | null>(null)
  const latest = useRef({ shot, vision, screen, insets, torch, room, onAutoSnap })
  useLayoutEffect(() => {
    latest.current = { shot, vision, screen, insets, torch, room, onAutoSnap }
  })

  // A new shot (or a retake) starts from scratch.
  useEffect(() => {
    generation.current++
    lockRef.current = initialLock(performance.now(), firstShot)
    bestRef.current = 0
    prevThumb.current = null
    boxRef.current = null
    boxAt.current = -Infinity
    scoreRef.current = null
    traceRef.current = null
    segTriedAt.current = -Infinity
    viewTriedAt.current = -Infinity
    fitRef.current = false
    glareRef.current = false
    viewRef.current = null
    tooClose.current = null
    roomHinted.current = false
    setView(IDLE_VIEW)
  }, [shot.id, attempt, firstShot])

  useEffect(() => {
    if (!video || !running) return
    // The run restarts after the walk card / review strip: re-arm the clock.
    lockRef.current = { ...lockRef.current, openedAt: performance.now() }

    // An answer from the engine: the car, its outline, the corner.
    const apply = (res: FrameResult, region: FrameSample["detectRegion"], roi: Box | null, corner: Corner | null) => {
      msRef.current = res.ms
      const inFrame = res.detections.map((d) => ({
        ...d,
        box: { x: region.x + d.box.x * region.w, y: region.y + d.box.y * region.h, w: d.box.w * region.w, h: d.box.h * region.h },
      }))
      const found = pickTarget(inFrame, boxRef.current)
      boxRef.current = found?.box ?? null
      boxAt.current = res.t
      scoreRef.current = found?.score ?? null
      const det0 = boxRef.current
      if (res.outline && roi && det0) {
        const points = new Float32Array(res.outline.length)
        for (let i = 0; i < points.length; i += 2) {
          points[i] = roi.x + res.outline[i] * roi.w
          points[i + 1] = roi.y + res.outline[i + 1] * roi.h
        }
        // Steady: line it up with the outline on screen and blend in,
        // unless it's a different shape altogether (then it jumps).
        const last = traceRef.current
        const shown = last && res.t - last.t <= SEGMENT.FRESH_MS ? followBox(last.points, last.at, det0) : null
        const aligned = shown ? alignTo(shown, points) : null
        const smooth = shown && aligned && outlineGap(shown, aligned) <= CONTOUR.JUMP ? blend(shown, aligned) : points
        traceRef.current = { t: res.t, points: smooth, at: det0 }
        aspectRef.current = nextCarAspect(aspectRef.current, boundsOf(smooth), { w: 1, h: 1 / currentAspect })
      }
      if (res.view && corner) {
        viewRef.current = { t: res.t, judgement: judgeLiveView(corner, res.view, null), ms: res.ms.view }
      }
    }
    // The visible frame's width over height (for the car's measured shape).
    let currentAspect = 1

    // Ask the engine for a look: the detect square always; a crop round the
    // car to trace; the whole frame for the corner check. Pictures are taken
    // now (the video moves on); the answer lands a moment later.
    const ask = (
      v: VisionClient,
      sample: FrameSample,
      trace: Box | null,
      check: boolean,
      corner: Corner | null,
    ) => {
      if (asking.current) return
      asking.current = true
      const mine = generation.current
      let roi: Box | null = null
      let point = { x: 0.5, y: 0.5 }
      let hint: Box = { x: 0, y: 0, w: 1, h: 1 }
      const pending: Array<Promise<ImageBitmap> | null> = [null, null, null]
      try {
        pending[0] = createImageBitmap(sample.detectCanvas)
        if (trace) {
          roi = cropAround(trace, sample.crop, SEGMENT.PAD)
          const canvas = (segCanvas.current ??= document.createElement("canvas"))
          canvas.width = SEGMENT.SIZE
          canvas.height = SEGMENT.SIZE
          const ctx = canvas.getContext("2d")
          if (ctx) {
            ctx.fillStyle = "#808080"
            ctx.fillRect(0, 0, SEGMENT.SIZE, SEGMENT.SIZE)
            drawCrop(ctx, video, sample.crop, roi, SEGMENT.SIZE)
            hint = { x: (trace.x - roi.x) / roi.w, y: (trace.y - roi.y) / roi.h, w: trace.w / roi.w, h: trace.h / roi.h }
            // Point at the car's body (a little below the middle of its box:
            // the hood or the doors, not the sky through the windows).
            point = { x: hint.x + hint.w / 2, y: hint.y + hint.h * 0.6 }
            pending[1] = createImageBitmap(canvas)
          }
        }
        if (check && video.videoWidth) {
          const canvas = (viewCanvas.current ??= document.createElement("canvas"))
          canvas.width = VIEW.WIDTH
          canvas.height = Math.max(1, Math.round((video.videoHeight * VIEW.WIDTH) / video.videoWidth))
          canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height)
          pending[2] = createImageBitmap(canvas)
        }
      } catch {
        // (Pictures already made are closed below.)
      }
      const region = sample.detectRegion
      void (async () => {
        try {
          const made = await Promise.allSettled(pending.map((p) => p ?? Promise.resolve(null)))
          const [detect, traceImage, viewImage] = made.map((m) => (m.status === "fulfilled" ? m.value : null))
          // A picture that couldn't be made: none of this look goes out.
          if (!detect || made.some((m) => m.status === "rejected")) {
            for (const image of [detect, traceImage, viewImage]) image?.close()
            return
          }
          const sentAt = performance.now()
          const res = await v.frame({
            t: sample.t,
            detect,
            ...(traceImage ? { segment: { image: traceImage, point, hint } } : {}),
            ...(viewImage ? { view: viewImage } : {}),
          })
          if (res) lookMs.current = performance.now() - sentAt
          if (res && generation.current === mine) apply(res, region, roi, corner)
        } catch {
          // A look that failed: the next sample asks again.
        } finally {
          asking.current = false
        }
      })()
    }

    const sampler = new FrameSampler(video, (sample: FrameSample) => {
      const { shot: s, vision: v, screen: scr, insets: ins, torch: hasTorch, room: rm, onAutoSnap: snap } = latest.current
      currentAspect = sample.crop.sw / Math.max(1, sample.crop.sh)
      const metrics: FrameMetrics = sample.metrics
      const motion = prevThumb.current ? motionBetween(prevThumb.current, sample.thumb) : Infinity
      prevThumb.current = sample.thumb
      // The sharpest view lately, fading over a few seconds: a close look at
      // the paint (lots of detail) must not make the whole car "blurry" after.
      bestRef.current = Math.max(metrics.sharpness, bestRef.current * BEST_FADE)
      const still = motion < QUALITY.STILL_MOTION
      const wide = s.kind === "wide"
      const useVision = !!v && (wide || s.kind === "closer")
      // The car as last found, unless that's old news (answers stopped
      // coming): never frame, lock or snap on a stale box.
      const freshFor = Math.min(DETECT_STALE_MAX_MS, Math.max(DETECT_FRESH_MS, 4 * (v?.intervalMs ?? 0), 2 * lookMs.current))
      const boxFresh = !!v && sample.t - boxAt.current <= freshFor
      const det0 = useVision && boxFresh ? boxRef.current : null

      if (useVision && v) {
        sampler.intervalMs = v.intervalMs
        // Trace the car about once a second; else, when it fits and the phone
        // is still, check the corner (never both in one look).
        const trace = wide && v.caps.segmenter && det0 && sample.t - segTriedAt.current >= SEGMENT.EVERY_MS ? det0 : null
        const check =
          !trace && wide && !!s.corner && v.caps.brain && fitRef.current && still && sample.t - viewTriedAt.current >= VIEW.EVERY_MS
        if (!asking.current) {
          if (trace) segTriedAt.current = sample.t
          if (check) viewTriedAt.current = sample.t
        }
        ask(v, sample, trace, check, s.corner ?? null)
      }

      // Their car as it is now: the fresh trace moved with the detector.
      const trace = traceRef.current && sample.t - traceRef.current.t <= SEGMENT.FRESH_MS ? traceRef.current : null
      const outline = trace && det0 ? followBox(trace.points, trace.at, det0) : null
      const traced = outline ? boundsOf(outline) : null
      // The outline counts only when it agrees with the detector on where the
      // car is (a trace of just a door or a wheel doesn't): otherwise it's not
      // drawn, and the detector's box alone decides.
      const agree = !!traced && !!det0 && iou(traced, det0) >= SEGMENT.AGREE
      // The fit is judged on the detector's box for this one car (cars parked
      // round it can't make it bigger); the traced outline catches a bumper
      // that is really cut off, when the car runs up to an edge of the picture.
      const carBox = det0
      const nearEdge = !!det0 && (det0.x < 0.05 || det0.y < 0.05 || det0.x + det0.w > 0.95 || det0.y + det0.h > 0.95)
      const cutOff = agree && nearEdge && isClipped(traced!)

      let target: TargetFrame | null = null
      let fit: Fit | null = null
      if (useVision && wide) {
        target = targetFrame(scr, ins, aspectRef.current ?? undefined)
        fit = judgeFit(carBox, cutOff, target, fitRef.current, {
          // (A car cut off by the frame has no shape to measure: a typical one.)
          car: TARGET.DEFAULT_CAR_ASPECT,
          screen: currentAspect,
        })
        fitRef.current = fit.pass
        if (fit.issue === "step-back") {
          const streak = tooClose.current && sample.t - tooClose.current.last <= TOO_CLOSE_GRACE_MS ? tooClose.current : null
          const now = fit.over ?? 1
          const over = streak ? streak.over + (now - streak.over) * OVER_EMA : now
          const since = streak?.since ?? (roomHinted.current ? sample.t - ROOM_HINT_MS : sample.t)
          const trail = [...(streak?.trail ?? []), { t: sample.t, over }].filter((p) => sample.t - p.t <= PROGRESS_MS + 500)
          let hintOver = streak?.hintOver ?? null
          if (hintOver === null && sample.t - since >= ROOM_HINT_MS && stuckIn(trail, sample.t)) hintOver = over
          else if (hintOver !== null && over < hintOver * HINT_CLEARS) hintOver = null
          if (hintOver !== null) roomHinted.current = true
          tooClose.current = { since, last: sample.t, over, level: stepBackLevel(streak?.level ?? null, over), trail, hintOver }
        } else if (fit.issue !== "no-car" || (tooClose.current && sample.t - tooClose.current.last > TOO_CLOSE_GRACE_MS)) {
          tooClose.current = null
        }
        if (fit.pass) lastFitAt.current = sample.t
        // Lost it for a while (walked off): the last reading no longer counts.
        else if (sample.t - lastFitAt.current > 1500) viewRef.current = null
      } else if (useVision) {
        fit = judgeCloser(det0)
      }

      const fresh = viewRef.current && sample.t - viewRef.current.t <= VIEW.FRESH_MS ? viewRef.current : null
      const lockView: LockView = fresh ? fresh.judgement.kind : !v?.caps.brain ? "unsure" : "pending"

      // A reflection over what the photo is for.
      const glareAt = glareArea(s.kind, carBox)
      const glare = glareAt ? glareShare(sample.gray, glareAt) : 0
      const glaring = glareIssue(glare, glareRef.current)
      glareRef.current = glaring

      let lock: LockState | null = null
      if (useVision && wide) {
        const result = stepLock(
          lockRef.current,
          {
            t: sample.t,
            hasCar: !!det0,
            fit: !!fit?.pass,
            view: lockView,
            still,
            sharp: !focusIssue(metrics, bestRef.current),
            lit: !lightIssue(metrics),
            glare: glaring,
            motion: Number.isFinite(motion) ? motion : 0,
          },
          { autoSnap: s.autoSnap, firstShot },
        )
        lockRef.current = result.state
        lock = result.state
        if (result.snap) snap()
      }

      const ms = msRef.current
      setView({
        coach: coachFor({
          t: sample.t,
          kind: s.kind,
          corner: s.corner ?? null,
          smart: useVision,
          metrics,
          bestSharpness: bestRef.current,
          still,
          fit,
          view: fresh?.judgement ?? null,
          lock,
          torch: hasTorch,
          glare: glaring,
          tooClose:
            tooClose.current && wide
              ? {
                  since: tooClose.current.since,
                  level: tooClose.current.level,
                  stuck: tooClose.current.hintOver !== null,
                  ...rm,
                }
              : null,
        }),
        box: det0,
        car: { outline: agree ? outline : null, box: carBox },
        target,
        fit,
        view: fresh?.judgement ?? null,
        checks: {
          spot: wide ? (useVision ? !!fit?.pass && lockView !== "wrong" && lockView !== "angle" : null) : null,
          sharp: !focusIssue(metrics, bestRef.current),
          light: !lightIssue(metrics) && !glaring,
        },
        lockPhase: lock?.phase ?? null,
        progress: lock ? lockProgress(lock, sample.t) : 0,
        debug: {
          luma: Math.round(metrics.luma),
          sharpness: Math.round(metrics.sharpness),
          motion: Number.isFinite(motion) ? Math.round(motion * 10) / 10 : -1,
          glare: Math.round(glare * 100) / 100,
          inferenceMs: ms?.detect != null ? Math.round(ms.detect) : null,
          score: scoreRef.current,
          segmentMs: ms?.segment != null ? Math.round(ms.segment) : null,
          viewMs: fresh?.ms != null ? Math.round(fresh.ms) : null,
          aspect: aspectRef.current ? Math.round(aspectRef.current * 100) / 100 : null,
        },
      })
    })
    sampler.start()
    return () => sampler.stop()
  }, [video, running, firstShot, shot.id, attempt])

  return view
}
